/**
 * ============================================================================
 *  File        : frontend/src/api/shipments.ts
 *  Module      : api
 *  Purpose     : 出貨 / 荷受け 型別化 API（S3）— shipment plan + receiving weight/details
 *  Author      : EF Engineering
 *  Created     : 2026-05-31
 *  Updated     : 2026-05-31
 *  License     : Proprietary - ECO FAITH Platform v3.0
 * ============================================================================
 *
 *  後端 handler：crates/api-http/src/routes/v12_collab_shipments.rs，掛在 v1：
 *    GET  /api/v1/cases/:id/shipment            -> get_current_for_case（envelope）
 *    POST /api/v1/cases/:id/shipment            -> plan_shipment（成員出貨計畫）
 *    POST /api/v1/shipments/:id/receiving-weight  -> record_receiving_weight（朝日過磅總重）
 *    POST /api/v1/shipments/:id/receiving-details -> record_receiving_details（單品荷受け）
 *  全部 RBAC gate = logistics.shipments.manage。
 *  Decimal 欄位（重量）一律以「字串」收送，前端不得轉 float。
 */

import { API_BASE, mutationHeaders, requestData, type Envelope } from './client'
import type { ApiSchemas } from './client'

/** 出貨明細 envelope payload（= 後端 V12ShipmentDetailDto）。 */
export type ShipmentDetailDto = ApiSchemas['V12ShipmentDetailDto']
/** 出貨主檔 DTO（= 後端 V12ShipmentDto）。 */
export type ShipmentDto = ApiSchemas['V12ShipmentDto']
/** 荷受け單品明細 DTO（= 後端 V12ReceivingItemDetailDto）。 */
export type ReceivingItemDetailDto = ApiSchemas['V12ReceivingItemDetailDto']
/** 重量稽核要件 DTO（= 後端 V12WeightAuditRequirementDto）。 */
export type WeightAuditRequirementDto = ApiSchemas['V12WeightAuditRequirementDto']
/** 成員出貨計畫 request body（= 後端 PlanV12ShipmentDto）。 */
export type PlanShipmentDto = ApiSchemas['PlanV12ShipmentDto']
/** 朝日荷受け總重 request body（= 後端 RecordV12ReceivingWeightDto）。 */
export type RecordReceivingWeightDto = ApiSchemas['RecordV12ReceivingWeightDto']
/** 朝日荷受け單品 request body（= 後端 RecordV12ReceivingDetailsDto）。 */
export type RecordReceivingDetailsDto = ApiSchemas['RecordV12ReceivingDetailsDto']
/** 單品荷受け輸入列（= 後端 V12ReceivingItemDetailInputDto）。 */
export type ReceivingItemDetailInputDto = ApiSchemas['V12ReceivingItemDetailInputDto']

const CASES_PATH = `${API_BASE}/cases`
const SHIPMENTS_PATH = `${API_BASE}/shipments`

/** 取得案件當前出貨明細（含 evidence / receiving_details / audit_requirements）。 */
export function getCaseShipment(caseId: number | string): Promise<ShipmentDetailDto> {
  return requestData<ShipmentDetailDto>(`${CASES_PATH}/${caseId}/shipment`, { method: 'GET' })
}

/** 成員建立 / 更新出貨計畫（vehicle / driver / shipped_at 等），回傳出貨明細。 */
export function planShipment(
  caseId: number | string,
  body: PlanShipmentDto,
): Promise<ShipmentDetailDto> {
  return requestData<ShipmentDetailDto>(`${CASES_PATH}/${caseId}/shipment`, {
    method: 'POST',
    body,
    headers: mutationHeaders(),
  })
}

/** 朝日荷受け：記錄会場過磅總重，回傳出貨明細。 */
export function recordReceivingWeight(
  shipmentId: number | string,
  body: RecordReceivingWeightDto,
): Promise<ShipmentDetailDto> {
  return requestData<ShipmentDetailDto>(`${SHIPMENTS_PATH}/${shipmentId}/receiving-weight`, {
    method: 'POST',
    body,
    headers: mutationHeaders(),
  })
}

/** 朝日荷受け：記錄單品確認（品名 / 重量 / 商品狀況），回傳出貨明細。 */
export function recordReceivingDetails(
  shipmentId: number | string,
  body: RecordReceivingDetailsDto,
): Promise<ShipmentDetailDto> {
  return requestData<ShipmentDetailDto>(`${SHIPMENTS_PATH}/${shipmentId}/receiving-details`, {
    method: 'POST',
    body,
    headers: mutationHeaders(),
  })
}

export type { Envelope }
